import {
  CommodityTypeList,
  ErrorBoundary,
  ListSelectionStyled,
  PlanningActionType,
  useAppUser,
  usePlanning,
} from '@bechtel/client-common';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Checkbox,
  FormControl,
  Icon,
  IconSize,
  Radio,
  Select,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import '@bechtel/gds/dist/index.css';
import { PersonaType } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Field, FieldArray, Form, Formik } from 'formik';
import { find, findIndex, isEqual, some } from 'lodash';
import { FC } from 'react';
import { MilestoneFlags } from '../drawer/milestone-flags/index';
import { ListSelectionHeadingStyled } from '../drawer/styled';
import DateRangeFilter from './date-range-filter';
import {
  dateRangeScope,
  DrawerProps,
  initialDrawerValues,
  searchByList,
  searchScopeList,
  sortByList,
  SortDirection,
} from './drawer-state';
import {
  DateListSelectionStyled,
  DrawerBackdropStyled,
  DrawerClose,
  DrawerFilterHeaderStyled,
  DrawerFooterButtonStyled,
  DrawerHeader,
  DrawerOuterStyled,
} from './styled';
import { UnassignedList } from './unassigned-list';

const SortSelectedItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" family="graphik">
      {i18n._(item.label)}
    </Text>
  );
};

const SortItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {i18n._(item.label)}
    </Text>
  );
};

export const Drawer: FC<DrawerProps> = ({ close, onSubmit, initialValues }) => {
  const { i18n } = useLingui();
  const { user } = useAppUser();
  const { planningDispatch, selectedWpList, selectedSuperintendent } = usePlanning();
  const wpDefaultDrawerValues =
    user &&
    user.crewMember &&
    user.crewMember.persona &&
    (user.crewMember.persona === PersonaType.FOREMAN ||
      user.crewMember.persona === PersonaType.GENERAL_FOREMAN ||
      (user.crewMember.persona === PersonaType.SUPERINTENDENT && user.crewMember.id === selectedSuperintendent?.id))
      ? initialDrawerValues
      : { ...initialDrawerValues, searchScope: false };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => {
        close();
        onSubmit(values);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ setValues, setFieldValue, isValid, dirty, values }) => {
        const isAsc = values.sortDir === SortDirection.ASC;

        return (
          <Form>
            <DrawerBackdropStyled data-testid="planning-wp-search-container">
              <DrawerOuterStyled>
                <DrawerHeader>
                  <DrawerClose onClick={close}></DrawerClose>
                </DrawerHeader>
                <SideSheetBody>
                  <ErrorBoundary>
                    <SideSheetContentCol>
                      <Box px={{ sm: '400' }} py={{ sm: '500' }}>
                        <Box mr={{ sm: '400' }} flexGrow={{ sm: 1 }} data-testid="planning-wp-search-sort-by">
                          <FormControl style={{ flexGrow: 1 }} label={t`Sort By`}>
                            <Select
                              size={TextFieldSize.MEDIUM}
                              appearance={TextFieldAppearance.NORMAL}
                              items={sortByList}
                              itemComponent={SortItem}
                              selectedComponent={SortSelectedItem}
                              initialSelectedItem={find(sortByList, { value: values.sortBy })}
                              onSelectedItemChange={(changes: any) => {
                                setFieldValue('sortBy', changes.selectedItem.value);
                              }}
                            />
                          </FormControl>
                        </Box>
                        <Box>
                          <Button
                            appearance={ButtonAppearance.SECONDARY}
                            size={ButtonSize.MEDIUM}
                            onClick={() => {
                              const newValue = isAsc ? SortDirection.DESC : SortDirection.ASC;
                              setFieldValue('sortDir', newValue);
                            }}
                          >
                            <Icon
                              fillColor="blue.800"
                              icon={isAsc ? 'sort-amount-down' : 'sort-amount-up'}
                              size={IconSize.MEDIUM}
                              data-testid="planning-wp-search-sort-icon"
                            />
                          </Button>
                        </Box>
                      </Box>
                      <DrawerFilterHeaderStyled>
                        <Trans>Work Packages</Trans>
                      </DrawerFilterHeaderStyled>
                      {searchScopeList.map((scope) => {
                        return (
                          <ListSelectionStyled
                            data-testid={`planning-wp-search-wp-assigned-${scope.id}`}
                            key={scope.id}
                            onClick={() => {
                              setFieldValue('searchScope', scope.value);
                            }}
                          >
                            <Box mr={{ sm: '500' }}>
                              <Radio checked={values.searchScope === scope.value} readOnly />
                            </Box>
                            <Text color="grey.1000" size="400">
                              {i18n._(scope.label)}
                            </Text>
                          </ListSelectionStyled>
                        );
                      })}
                      <DrawerFilterHeaderStyled>
                        <Trans>Search By</Trans>
                      </DrawerFilterHeaderStyled>
                      <FieldArray name="searchBy">
                        {(arrayHelpers) => {
                          return (
                            <>
                              {searchByList
                                .filter((s) => s.scope.includes(values.searchScope))
                                .map((search) => {
                                  return (
                                    <ListSelectionStyled
                                      key={`${search.searchByTerm.type}`}
                                      onClick={() => {
                                        const selected = findIndex(values.searchBy, (searchItem) => {
                                          return searchItem.type === search.searchByTerm.type;
                                        });
                                        if (selected !== -1) {
                                          arrayHelpers.remove(selected);
                                        } else {
                                          arrayHelpers.push(search.searchByTerm);
                                        }
                                      }}
                                    >
                                      <Box mr={{ sm: '500' }}>
                                        <Checkbox
                                          data-testid={`search-by-${search.searchByTerm.type}`}
                                          checked={some(values.searchBy, (searchItem) => {
                                            return searchItem.type === search.searchByTerm.type;
                                          })}
                                          readOnly
                                        />
                                      </Box>
                                      <Text color="grey.1000" size="400">
                                        {i18n._(search.label)}
                                      </Text>
                                    </ListSelectionStyled>
                                  );
                                })}
                            </>
                          );
                        }}
                      </FieldArray>
                      <DrawerFilterHeaderStyled>
                        <Trans>Filter By</Trans>
                      </DrawerFilterHeaderStyled>
                      <ListSelectionHeadingStyled data-testid="date-range">
                        <Box flexGrow={{ sm: 1 }} justifyContent={{ sm: 'space-between' }}>
                          <Text color="grey.1000" size="400" weight="600">
                            <Trans>Date Range</Trans>
                          </Text>
                          <Text color="grey.1000" size="400" data-testid="selected-length">{t`${
                            values.dateScope ? 1 : 0
                          } Selected`}</Text>
                        </Box>
                      </ListSelectionHeadingStyled>
                      {dateRangeScope.map((dateItem) => {
                        return (
                          <DateListSelectionStyled
                            data-testid={`planning-wp-search-date-scope-${dateItem.id}`}
                            key={dateItem.id}
                            onClick={() => {
                              setFieldValue('dateScope', dateItem.value);
                            }}
                          >
                            <Box>
                              <Box mr={{ sm: '500' }}>
                                <Radio checked={values.dateScope === dateItem.value} readOnly />
                              </Box>
                              <Text color="grey.1000" size="400">
                                {i18n._(dateItem.label)}
                              </Text>
                            </Box>
                            {values.dateScope === dateItem.value && values.dateScope === 'SCHEDULED' && (
                              <DateRangeFilter />
                            )}
                          </DateListSelectionStyled>
                        );
                      })}
                      <Field name="commodityTypeId" component={CommodityTypeList} />
                      <Field name="milestoneFlagId" component={MilestoneFlags} />
                      <Field name="unassignedId" component={UnassignedList} />
                      <Box p={{ sm: '500' }}>
                        <Button
                          css={`
                            color: ${({ theme }) => theme.colors.blue[800]};
                            font-weight: ${({ theme }) => theme.fontWeights[600]};
                          `}
                          size={ButtonSize.MEDIUM}
                          appearance={ButtonAppearance.SECONDARY}
                          data-testid="planning-wp-search-reset"
                          type="reset"
                          disabled={isEqual(wpDefaultDrawerValues, values)}
                          onClick={() => {
                            setValues(wpDefaultDrawerValues);
                            if (selectedWpList && selectedWpList.length > 0) {
                              planningDispatch({
                                type: PlanningActionType.ResetWorkPackageSelection,
                                payload: { selectedWpList: [] },
                              });
                            }
                          }}
                        >
                          <Trans>Reset All Filters</Trans>
                        </Button>
                      </Box>
                    </SideSheetContentCol>
                  </ErrorBoundary>
                </SideSheetBody>
                <SideSheetFooter>
                  <Box justifyContent={{ sm: 'flex-end' }}>
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        data-testid="planning-wp-search-cancel"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={close}
                      >
                        <Trans>Cancel</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                    <Box>
                      <DrawerFooterButtonStyled
                        type="submit"
                        size={ButtonSize.MEDIUM}
                        appearance={ButtonAppearance.SECONDARY}
                        disabled={!dirty || !isValid}
                        data-testid="planning-wp-search-done"
                      >
                        <Trans>Apply Filters</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  </Box>
                </SideSheetFooter>
              </DrawerOuterStyled>
            </DrawerBackdropStyled>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Drawer;
